import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { EditableField } from '../ui/EditableField';
import { typedSiegeAddress, withPickedLocation, withSiegeAddress } from '../../utils/siegeAddress';
import { PhoneField, isPlaceholderPhone, phoneError } from '../ui/PhoneField';
import { TagInput } from '../ui/TagInput';
import { SocialLinksInput } from '../ui/SocialLinksInput';
import { LocationPicker } from '../ui/LocationPicker';
import { MultiLocationPicker } from '../ui/MultiLocationPicker';
import { PitchSection } from '../ui/PitchSection';
import { PersonInput } from '../ui/PersonInput';
import { AssetInput } from '../ui/AssetInput';
import { StockInput } from '../ui/StockInput';
import FileUpload from '../ui/FileUpload';
import FinancialInput from '../ui/FinancialInput';
import { CompanyLogo } from './CompanyLogo';
import ProfessionalPDFExtractionButton from '../pdf/ProfessionalPDFExtractionButton';
import { useUser } from '../../hooks/useUser';
import { useFormSecurity } from '../../hooks/useFormSecurity';
import { useToast } from '../../hooks/useToast';
import { useCompany } from '../../hooks/useCompany';
import { useEntityVocabulary } from '../../hooks/useEntityVocabulary';
import { parseEntityVariant, type EntityVariant } from '../../types/common';
import { AvecIdentificationSection } from './AvecIdentificationSection';
import {
  EMPTY_AVEC_VALUES,
  buildAvecCompanyPayload,
  readAvecDetails,
  verifiedAccountEmail,
  type AvecFormValues,
} from './avecProfile';
import { getProvinces, getCities } from '../../constants/jurisdictions';
import { customerApiService } from '../../services/customerApi';
import {
  formDraftKey,
  saveFormDraft,
  loadFormDraft,
  clearFormDraft,
} from '../../utils/formDraft';
import { Edit3, Save, X, Loader2, Scale, FolderOpen, Building2, Info } from 'lucide-react';
import {
  getLegalFormsForVariant,
  COMPANY_TYPES,
  TRADITIONAL_SECTORS,
  STARTUP_SECTORS,
  TECHNOLOGY_READINESS_LEVELS,
  BUSINESS_MODELS,
  YES_NO_OPTIONS,
  CURRENCIES,
  getCompanySizesForVariant,
  ACCOMPANIMENT_TYPES
} from '../../constants/enterpriseFormOptions';

import {
  itemsForSection,
  mergeFinancingSection,
  normalizeBankAccounts,
  normalizeFinancings,
  type FinancingSection,
} from '../../utils/financialRecords';
import type {
  Address,
  AssetData,
  BankAccount,
  Company,
  Coordinates,
  DocumentFile,
  Financing,
  Insurance,
  PersonData,
  PitchData,
  SocialLink,
  StockData,
} from '../../types/company';

/**
 * Champs que le serveur renvoie avec l'entreprise mais que le type partagé
 * Company ne décrit pas (encore).
 */
type CompanyServerExtras = {
  variant?: unknown;
  address?: Address;
  avecDetails?: unknown;
  customerId?: string;
  telephoneContact?: string;
};



/**
 * Contact de l'entreprise, celui propage dans toutes les applications
 * (Customer.phone cote serveur). Pour un compte existant sans numero mobile a
 * lui, on reprend ce contact propage, valeur par defaut comprise, plutot que
 * d'afficher un champ vide qui ne correspondrait pas a ce que voient le
 * back-office et les apps.
 */
const contactPrincipal = (company: (Company & CompanyServerExtras) | null | undefined): string => {
  const value =
    company?.telephoneMobile ||
    (!company?.telephoneFixe ? company?.telephoneContact : '') ||
    '';
  // L'ancienne valeur d'attente « +243 000 000 000 » n'est pas un numéro saisi.
  return isPlaceholderPhone(value) ? '' : value;
};

const EnterpriseIdentificationForm: React.FC = () => {
  const { user, isLoading: userLoading } = useUser();
  const { showToast } = useToast();
  // updateCompany cree l entreprise quand il n y en a pas encore (voir useCompany) :
  // un seul chemin, qu il s agisse d une creation ou d une mise a jour.
  const { company, updateCompany, loading: companyLoading } = useCompany(user?.companyId);
  const companyExtras = company as (Company & CompanyServerExtras) | null;
  // Email du compte, seulement s'il est vérifié : contact par défaut de
  // l'entreprise quand aucun n'est enregistré (jamais imposé, modifiable).
  const accountEmail = verifiedAccountEmail(user);
  // Vocabulaire adaptatif : entreprise / coopérative selon le variant courant.
  // Source : company.variant (backend) ou sessionStorage.signup_variant (onboarding).
  const vocab = useEntityVocabulary();
  // Sécurité des soumissions : détection d'injections (SQL et prompt, car ces
  // profils nourrissent Adha), nettoyage des chaînes et limitation de débit.
  // Le jeton CSRF n'est pas activé : le backend s'appuie sur le JWT, en
  // produire un ici ne protégerait de rien.
  const {
    validateFormSecurity,
    sanitizeFormInput,
  } = useFormSecurity({
    enableCsrf: false,
    detectSqlInjection: true,
    detectPromptInjection: true,
    enableRateLimit: true,
    rateLimitAction: 'formSubmit',
    rateLimitIdentifier: user?.id || 'anonymous',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ─────────────────────────────────────────────────────────────────────
  // Inline variant selector — visible TANT QUE le profil n'a pas été
  // soumis pour validation.
  // ─────────────────────────────────────────────────────────────────────
  // Bug fix : auparavant le selector n'apparaissait que si user.companyId
  // était null. Or customer-service.syncUser auto-crée silencieusement la
  // Company au premier /users/me, AVANT que l'utilisateur n'atteigne ce
  // form. user.companyId était donc déjà renseigné → selector caché →
  // variant resté 'standard' par défaut même pour les vrais coopératives.
  //
  // Nouveau critère : le selector reste visible tant que la company est
  // en brouillon (statutValidation = 'brouillon'). Une fois soumise pour
  // validation, le variant est verrouillé (PME = sme/standard,
  // Coopérative = sme/cooperative).
  const hasNoCompany = !user?.companyId || user.companyId.trim() === '';
  const isDraftCompany =
    company?.statutValidation === 'brouillon' ||
    !company?.statutValidation;
  const isNewAccount = hasNoCompany || isDraftCompany;
  // Lit l'état initial : priorité à company.variant si on en a un,
  // sinon sessionStorage (flow /register?variant=cooperative), sinon
  // 'standard'. Garantit que le toggle reflète toujours la vérité backend.
  const [pendingVariant, setPendingVariant] = useState<EntityVariant>(() => {
    const fromCompany = parseEntityVariant(companyExtras?.variant);
    if (fromCompany) return fromCompany;
    if (typeof window === 'undefined') return 'standard';
    try {
      return parseEntityVariant(sessionStorage.getItem('signup_variant')) || 'standard';
    } catch {
      return 'standard';
    }
  });

  // Synchronise pendingVariant avec company.variant quand la company se
  // charge après le premier render (cas où le hook useCompany résout
  // asynchroniquement).
  useEffect(() => {
    const v = parseEntityVariant(companyExtras?.variant);
    if (v && v !== pendingVariant) {
      setPendingVariant(v);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, companyExtras?.variant]);

  const handleVariantChange = (next: EntityVariant) => {
    setPendingVariant(next);
    try {
      sessionStorage.setItem('signup_variant', next);
    } catch {
      // sessionStorage indisponible (privacy mode) — ignore, la valeur
      // sera quand même incluse dans le payload au submit via formData.
    }
    // Force un re-render du hook useCompanyVariant en émettant un storage
    // event manuel (les composants frères qui dépendent de useEntityVocabulary
    // se rafraîchissent ainsi sans prop drilling).
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'signup_variant',
        newValue: next,
      }));
    }
  };
  
  // ✨ ANTI-VIBRATION: Ref pour tracker l'ID de l'entreprise déjà initialisée
  const initializedCompanyIdRef = React.useRef<string | null>(null);

  // ── Persistance du BROUILLON (anti-perte de données) ────────────────────
  // Clé cloisonnée par entreprise + utilisateur. Le brouillon sauvegarde les
  // saisies non encore « Sauvegardées » côté backend, pour survivre à un
  // rafraîchissement de page ou au démontage du formulaire (changement d'onglet
  // / navigation).
  const draftKey = React.useMemo(
    () => formDraftKey('company', user?.companyId || company?.id, user?.id),
    [user?.companyId, company?.id, user?.id],
  );
  const draftRestoredRef = React.useRef(false);
  
  // État local pour le formulaire
  const [formData, setFormData] = useState({
    // Identification de base
    raisonSociale: '',
    sigle: '',
    formeJuridiqueOHADA: '',
    dateCreation: '',
    dateDebutActivites: '',
    numeroRCCM: '',
    numeroIdentificationNationale: '',
    numeroImpotFiscal: '',

    // Juridiction (localisation administrative) — complémentaire au GPS.
    // Persistée sur le profil CLIENT (Customer.address.province/city) pour
    // l'isolation régionale admin et le placement sur cartes. Optionnelle.
    province: '',
    ville: '',
    // Fiche AVEC (company.avecDetails), formulaire réduit
    avec: { ...EMPTY_AVEC_VALUES },

    // Nature des activités
    typeEntreprise: 'traditional' as 'traditional' | 'startup',
    secteurActivitePrincipal: '',
    secteursActiviteSecondaires: [] as string[],
    secteursPersonalises: [] as string[], // Secteurs ajoutés par l'entreprise
    descriptionActivites: '',
    produitsServices: [] as string[],
    
    // Informations complémentaires
    tailleEntreprise: '',
    capitalSocial: '',
    deviseCapital: 'USD' as 'USD' | 'CDF' | 'EUR',
    
    // Incubation et accélération (optionnel)
    enIncubation: false,
    typeAccompagnement: '' as 'incubation' | 'acceleration' | '',
    nomIncubateurAccelerateur: '',
    certificatAffiliation: [] as DocumentFile[], // Documents PDF
    
    // Coordonnées et localisation
    siegeSocial: null as Coordinates | null,
    siegeExploitation: null as Coordinates | null,
    unitesProduction: [] as Coordinates[],
    pointsVente: [] as Coordinates[],
    
    // Contacts et réseaux sociaux
    telephoneFixe: '',
    telephoneMobile: '',
    fax: '',
    email: '',
    boitePostale: '',
    reseauxSociaux: [] as SocialLink[],
    
    // Pitch et présentation
    pitch: {
      elevator_pitch: '',
      value_proposition: '',
      target_market: '',
      competitive_advantage: '',
      pitch_deck_url: '',
      demo_video_url: ''
    } as PitchData,
    
    // Spécificités startup
    niveauMaturiteTechnologique: '',
    modeleEconomique: '',
    proprieteIntellectuelle: [] as string[],
    
    // Spécificités traditionnelles
    certificationQualite: false,
    licencesExploitation: [] as string[],
    
    // Structure organisationnelle
    dirigeants: [] as PersonData[],
    employes: [] as PersonData[],
    actionnaires: [] as PersonData[],
    nombreEmployes: 0,
    organigramme: '',
    
    // Patrimoine et moyens
    immobilisations: [] as AssetData[],
    equipements: [] as AssetData[],
    vehicules: [] as AssetData[],
    stocks: [] as StockData[],
    moyensTechniques: [] as string[],
    capaciteProduction: '',
    
    // Financier et juridique
    comptesBancaires: [] as BankAccount[],
    assurances: [] as Insurance[],
    financements: [] as Financing[],
    litigesEnCours: false,
    detailsLitiges: '',
    failliteAnterieure: false,
    detailsFaillite: '',
    poursuiteJudiciaire: false,
    detailsPoursuites: '',
    garantiePrets: false,
    detailsGaranties: '',
    antecedentesFiscales: false,
    detailsAntecedentes: '',
    
    // Documents obligatoires
    documentsEntreprise: [] as DocumentFile[],
    documentsPersonnel: [] as DocumentFile[],
    documentsFinanciers: [] as DocumentFile[],
    documentsPatrimoine: [] as DocumentFile[],
    documentsProprietéIntellectuelle: [] as DocumentFile[],
    documentsSectoriels: [] as DocumentFile[],
  });

  // Email de contact pré-rempli une seule fois avec l'email vérifié du compte
  // quand l'entreprise n'en a pas (compte chargé après le formulaire, nouveau
  // compte) ; un email effacé ensuite par l'utilisateur n'est pas remis.
  const emailPrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (emailPrefilledRef.current || !accountEmail || company?.email) return;
    emailPrefilledRef.current = true;
    setFormData(prev => (prev.email ? prev : { ...prev, email: accountEmail }));
  }, [accountEmail, company?.email]);

  // ✨ SIMPLIFICATION: useEffect pour charger les données
  React.useEffect(() => {
    if (!company?.id || initializedCompanyIdRef.current === company.id) {
      return;
    }
    
    console.log('📥 Chargement des données dans le formulaire:', company.raisonSociale);
    initializedCompanyIdRef.current = company.id;
    
    // Helper pour copie profonde des tableaux d'objets (évite les mutations croisées)
    const deepCopyArray = <T extends object>(arr: T[] | undefined | null): T[] => {
      if (!arr || !Array.isArray(arr)) return [];
      return arr.map(item => ({ ...item }));
    };
    const uploadedDocs = (docs: DocumentFile[] | undefined): DocumentFile[] => deepCopyArray(docs);

    setFormData(prev => ({
      ...prev,
        // Identification de base
        raisonSociale: company.raisonSociale || '',
        sigle: company.sigle || '',
        formeJuridiqueOHADA: company.formeJuridiqueOHADA || '',
        dateCreation: company.dateCreation || '',
        dateDebutActivites: company.dateDebutActivites || '',
        numeroRCCM: company.numeroRCCM || '',
        numeroIdentificationNationale: company.numeroIdentificationNationale || '',
        numeroImpotFiscal: company.numeroImpotFiscal || '',

        // Juridiction (depuis l'adresse structurée du client si présente)
        province: companyExtras?.address?.province || '',
        ville: companyExtras?.address?.city || '',
        avec: readAvecDetails(companyExtras?.avecDetails),

        // Nature des activités
        typeEntreprise: company.typeEntreprise || 'traditional',
        secteurActivitePrincipal: company.secteurActivitePrincipal || '',
        secteursActiviteSecondaires: company.secteursActiviteSecondaires || [],
        secteursPersonalises: company.secteursPersonalises || [],
        descriptionActivites: company.descriptionActivites || '',
        produitsServices: company.produitsServices || [],

        // Informations complémentaires
        tailleEntreprise: company.tailleEntreprise || '',
        capitalSocial: company.capitalSocial || '',
        deviseCapital: company.deviseCapital || 'USD',

        // Incubation et accélération - COPIE PROFONDE
        enIncubation: company.enIncubation || false,
        typeAccompagnement: company.typeAccompagnement || '',
        nomIncubateurAccelerateur: company.nomIncubateurAccelerateur || '',
        certificatAffiliation: uploadedDocs(company.certificatAffiliation),
        
        // Coordonnées et localisation - COPIE PROFONDE pour tableaux d'objets
        siegeSocial: company.siegeSocial || null,
        siegeExploitation: company.siegeExploitation || null,
        unitesProduction: deepCopyArray(company.unitesProduction),
        pointsVente: deepCopyArray(company.pointsVente),
        
        // Contacts et réseaux sociaux - COPIE PROFONDE
        telephoneFixe: company.telephoneFixe || '',
        telephoneMobile: contactPrincipal(company),
        fax: company.fax || '',
        email: company.email || accountEmail,
        boitePostale: company.boitePostale || '',
        reseauxSociaux: deepCopyArray(company.reseauxSociaux),
        
        // Pitch et présentation
        pitch: company.pitch || {
          elevator_pitch: '',
          value_proposition: '',
          target_market: '',
          competitive_advantage: '',
          pitch_deck_url: '',
          demo_video_url: ''
        },
        
        // Spécificités startup - COPIE PROFONDE
        niveauMaturiteTechnologique: company.niveauMaturiteTechnologique || '',
        modeleEconomique: company.modeleEconomique || '',
        proprieteIntellectuelle: [...(company.proprieteIntellectuelle || [])],
        
        // Spécificités traditionnelles
        certificationQualite: company.certificationQualite || false,
        licencesExploitation: company.licencesExploitation || [],
        
        // Structure organisationnelle - COPIE PROFONDE pour éviter mutations croisées
        dirigeants: deepCopyArray(company.dirigeants),
        employes: deepCopyArray(company.employes),
        actionnaires: deepCopyArray(company.actionnaires),
        nombreEmployes: company.nombreEmployes || 0,
        organigramme: company.organigramme || '',
        
        // Patrimoine et moyens - COPIE PROFONDE
        immobilisations: deepCopyArray(company.immobilisations),
        equipements: deepCopyArray(company.equipements),
        vehicules: deepCopyArray(company.vehicules),
        stocks: deepCopyArray(company.stocks),
        moyensTechniques: company.moyensTechniques || [],
        capaciteProduction: company.capaciteProduction || '',
        
        // Financier et juridique - COPIE PROFONDE
        comptesBancaires: normalizeBankAccounts(company.comptesBancaires),
        assurances: deepCopyArray(company.assurances),
        financements: normalizeFinancings(company.financements),
        litigesEnCours: company.litigesEnCours || false,
        detailsLitiges: company.detailsLitiges || '',
        failliteAnterieure: company.failliteAnterieure || false,
        detailsFaillite: company.detailsFaillite || '',
        poursuiteJudiciaire: company.poursuiteJudiciaire || false,
        detailsPoursuites: company.detailsPoursuites || '',
        garantiePrets: company.garantiePrets || false,
        detailsGaranties: company.detailsGaranties || '',
        antecedentesFiscales: company.antecedentesFiscales || false,
        detailsAntecedentes: company.detailsAntecedentes || '',
        
        // Documents obligatoires - COPIE PROFONDE
        documentsEntreprise: uploadedDocs(company.documentsEntreprise),
        documentsPersonnel: uploadedDocs(company.documentsPersonnel),
        documentsFinanciers: uploadedDocs(company.documentsFinanciers),
        documentsPatrimoine: uploadedDocs(company.documentsPatrimoine),
        documentsProprietéIntellectuelle: uploadedDocs(company.documentsProprietéIntellectuelle),
        documentsSectoriels: uploadedDocs(company.documentsSectoriels),
      }));
    console.log('✅ Données initialisées dans le formulaire');
  }, [company?.id]); // Dépendance uniquement sur l'ID

  // ── Restauration du brouillon (une seule fois) ──────────────────────────
  // S'exécute APRÈS l'initialisation depuis le backend (les deux effets se
  // succèdent sur le même cycle une fois la company chargée) : on superpose
  // les saisies non sauvegardées. Pour un compte sans entreprise encore créée,
  // on n'attend pas la company. Si un brouillon est restauré, on repasse en
  // mode édition pour que l'utilisateur puisse poursuivre et « Sauvegarder ».
  React.useEffect(() => {
    if (draftRestoredRef.current) return;
    // Attendre l'utilisateur : au premier rendu il n'est pas encore charge, la
    // cle valait `company:new:anon` (vide), la restauration se marquait faite
    // et ne lisait JAMAIS le vrai brouillon, que la sauvegarde automatique
    // ecrasait ensuite avec le formulaire vierge. C'etait la saisie « effacee ».
    if (userLoading || !user?.id) return;
    // Si l'utilisateur a une entreprise, attendre que l'init backend soit faite.
    if (user?.companyId && initializedCompanyIdRef.current !== company?.id) return;

    const draft = loadFormDraft<Partial<typeof formData>>(draftKey);
    draftRestoredRef.current = true;
    if (draft && Object.keys(draft).length > 0) {
      console.log('♻️ Brouillon du formulaire restauré');
      // Un brouillon ancien peut porter les formes d'avant l'alignement sur le
      // DTO : comptes et financements sont relus de façon tolérante.
      setFormData(prev => ({
        ...prev,
        ...draft,
        ...('comptesBancaires' in draft ? { comptesBancaires: normalizeBankAccounts(draft.comptesBancaires) } : {}),
        ...('financements' in draft ? { financements: normalizeFinancings(draft.financements) } : {}),
      }));
      setIsEditing(true);
    }
  }, [draftKey, company?.id, user?.companyId, user?.id, userLoading]);

  // L'entreprise existe-t-elle deja cote serveur ? Avant, seul l'onglet
  // Général compte (nom et forme juridique suffisent a la creer) : les autres
  // s'ouvrent une fois la premiere sauvegarde faite.
  const entrepriseCreee = !!(user?.companyId || (company?.id && !company.id.includes('temp')));

  // Nouvelle entreprise : le formulaire s'ouvre directement en saisie, sans
  // passer par « Éditer le profil ». Une seule fois, pour qu'Annuler reste
  // respecte.
  const saisieOuverteRef = React.useRef(false);
  React.useEffect(() => {
    if (saisieOuverteRef.current || userLoading || !user?.id || entrepriseCreee) return;
    saisieOuverteRef.current = true;
    setIsEditing(true);
  }, [userLoading, user?.id, entrepriseCreee]);

  // ── Sauvegarde automatique du brouillon ─────────────────────────────────
  // Uniquement en mode édition, pour ne pas persister l'état vierge chargé
  // depuis le backend. Chaque modification de formData est ainsi conservée.
  React.useEffect(() => {
    if (!isEditing) return;
    saveFormDraft(draftKey, formData);
  }, [formData, isEditing, draftKey]);
  
  // ✨ Calculer l'état d'initialisation
  const isFormInitialized = !user?.companyId || initializedCompanyIdRef.current === company?.id;

  // Gestion du mode édition
  const handleStartEditing = () => setIsEditing(true);
  
  const handleCancelEditing = () => {
    setIsEditing(false);
    // Annulation explicite → on abandonne le brouillon local.
    clearFormDraft(draftKey);
    // Restaurer TOUTES les données depuis company
    if (company) {
      console.log('🔄 Restauration des données originales depuis le backend');
      
      setFormData(prev => ({
        ...prev,
        // Identification de base
        raisonSociale: company.raisonSociale || '',
        sigle: company.sigle || '',
        formeJuridiqueOHADA: company.formeJuridiqueOHADA || '',
        dateCreation: company.dateCreation || '',
        dateDebutActivites: company.dateDebutActivites || '',
        numeroRCCM: company.numeroRCCM || '',
        numeroIdentificationNationale: company.numeroIdentificationNationale || '',
        numeroImpotFiscal: company.numeroImpotFiscal || '',

        // Juridiction (restaure depuis l'adresse structurée du client)
        province: companyExtras?.address?.province || '',
        ville: companyExtras?.address?.city || '',
        avec: readAvecDetails(companyExtras?.avecDetails),

        // Nature des activités
        typeEntreprise: company.typeEntreprise || 'traditional',
        secteurActivitePrincipal: company.secteurActivitePrincipal || '',
        secteursActiviteSecondaires: company.secteursActiviteSecondaires || [],
        secteursPersonalises: company.secteursPersonalises || [],
        descriptionActivites: company.descriptionActivites || '',
        produitsServices: company.produitsServices || [],

        // Informations complémentaires
        tailleEntreprise: company.tailleEntreprise || '',
        capitalSocial: company.capitalSocial || '',
        deviseCapital: company.deviseCapital || 'USD',
        
        // Contacts
        telephoneFixe: company.telephoneFixe || '',
        telephoneMobile: contactPrincipal(company),
        fax: company.fax || '',
        email: company.email || accountEmail,
        boitePostale: company.boitePostale || '',


        // ... tous les autres champs
      }));
    }
  };

  // AVEC : formulaire réduit (cahier des charges 4.4) à la place des onglets.
  const effectiveVariant: EntityVariant = isNewAccount
    ? pendingVariant
    : parseEntityVariant(companyExtras?.variant) || vocab.variant;
  const isAvec = effectiveVariant === 'avec';
  // Email de contact de l'AVEC : celui de l'entreprise (formData.email),
  // sinon celui du compte s'il est vérifié. Facultatif, modifiable.
  const avecValuesOf = (data: typeof formData): AvecFormValues => ({
    raisonSociale: data.raisonSociale || '',
    ...data.avec,
    email: data.email || accountEmail,
  });
  const handleAvecFieldChange = (patch: Partial<AvecFormValues>) => {
    if (!isEditing) return;
    const { raisonSociale, email, ...avec } = patch;
    setFormData(prev => ({
      ...prev,
      ...(raisonSociale !== undefined ? { raisonSociale: String(raisonSociale) } : {}),
      ...(email !== undefined ? { email } : {}),
      avec: { ...prev.avec, ...avec },
    }));
  };

  const handleSaveProfile = async () => {
    // Un seul envoi à la fois (double clic, Entrée répétée).
    if (isSaving) return;
    // Garde de sécurité avant tout envoi : une saisie piégée ne doit pas
    // atteindre le backend, ni l'assistant qui lira ce profil plus tard.
    const security = validateFormSecurity(formData);
    if (!security.isValid) {
      showToast({
        type: 'error',
        title: 'Saisie refusée',
        message: security.errors[0] || 'Votre saisie contient un contenu non autorisé.',
      });
      return;
    }
    // AVEC : programme d'appui facultatif ; sans choix, la structure d'appui
    // par défaut (« Réseau ikiotahub ») suit le groupe, rattachée par le serveur.
    const safeData = sanitizeFormInput(formData);
    // AVEC : le téléphone du président(e) est le contact que voit sa
    // structure d'appui. Un numéro mal formé n'est pas envoyé ; le formulaire
    // et son brouillon restent tels quels pour le corriger.
    const avecPhoneError = isAvec ? phoneError(safeData.avec?.presidentPhone) : null;
    if (avecPhoneError) {
      showToast({ type: 'error', title: 'Téléphone du président ou de la présidente', message: avecPhoneError });
      return;
    }
    setIsSaving(true);
    try {
      // ✨ NOUVEAU: Transmettre TOUTES les données du formulaire - pas de mapping complexe
      // Le formulaire est la source de vérité, on envoie tout directement
      // Variant orthogonal au type : « cooperative » ou « standard ». Pour un
      // nouveau compte, le selecteur affiche fait foi ; sinon on garde celui
      // retenu a l'inscription. On ne l'envoie jamais a vide, pour ne pas
      // ecraser par accident celui deja enregistre cote serveur.
      const sessionVariant = typeof window !== 'undefined'
        ? parseEntityVariant(sessionStorage.getItem('signup_variant'))
        : null;
      const signupVariant: EntityVariant | null = isNewAccount
        ? pendingVariant
        : sessionVariant;

      // AVEC : formulaire réduit, seuls ses champs partent (aucune chaîne vide).
      const updateData = isAvec ? buildAvecCompanyPayload(avecValuesOf(safeData)) : {
        ...(signupVariant ? { variant: signupVariant } : {}),
        // ============================================
        // SECTION: Identification de Base
        // ============================================
        raisonSociale: safeData.raisonSociale,
        sigle: safeData.sigle,
        formeJuridiqueOHADA: safeData.formeJuridiqueOHADA,
        dateCreation: safeData.dateCreation,
        dateDebutActivites: safeData.dateDebutActivites,
        numeroRCCM: safeData.numeroRCCM,
        numeroIdentificationNationale: safeData.numeroIdentificationNationale,
        numeroImpotFiscal: safeData.numeroImpotFiscal,
        
        // ============================================
        // SECTION: Nature des Activités
        // ============================================
        typeEntreprise: safeData.typeEntreprise,
        secteurActivitePrincipal: safeData.secteurActivitePrincipal,
        secteursActiviteSecondaires: safeData.secteursActiviteSecondaires,
        secteursPersonalises: safeData.secteursPersonalises,
        descriptionActivites: safeData.descriptionActivites,
        produitsServices: safeData.produitsServices,
        
        // ============================================
        // SECTION: Informations Complémentaires
        // ============================================
        tailleEntreprise: safeData.tailleEntreprise,
        capitalSocial: safeData.capitalSocial,
        deviseCapital: safeData.deviseCapital,
        
        // ============================================
        // SECTION: Incubation et Accélération
        // ============================================
        enIncubation: safeData.enIncubation,
        typeAccompagnement: safeData.typeAccompagnement,
        nomIncubateurAccelerateur: safeData.nomIncubateurAccelerateur,
        certificatAffiliation: safeData.certificatAffiliation,
        
        // ============================================
        // SECTION: Coordonnées et Localisation
        // ============================================
        siegeSocial: safeData.siegeSocial,
        siegeExploitation: safeData.siegeExploitation,
        unitesProduction: safeData.unitesProduction,
        pointsVente: safeData.pointsVente,
        
        // ============================================
        // SECTION: Contacts et Réseaux Sociaux
        // ============================================
        telephoneFixe: safeData.telephoneFixe,
        telephoneMobile: safeData.telephoneMobile,
        fax: safeData.fax,
        email: safeData.email,
        boitePostale: safeData.boitePostale,
        reseauxSociaux: safeData.reseauxSociaux,
        
        // ============================================
        // SECTION: Pitch et Présentation
        // ============================================
        pitch: safeData.pitch,
        
        // ============================================
        // SECTION: Spécificités Startup
        // ============================================
        niveauMaturiteTechnologique: safeData.niveauMaturiteTechnologique,
        modeleEconomique: safeData.modeleEconomique,
        proprieteIntellectuelle: safeData.proprieteIntellectuelle,
        
        // ============================================
        // SECTION: Spécificités Traditionnelles
        // ============================================
        certificationQualite: safeData.certificationQualite,
        licencesExploitation: safeData.licencesExploitation,
        
        // ============================================
        // SECTION: Structure Organisationnelle
        // ============================================
        dirigeants: safeData.dirigeants,
        employes: safeData.employes,
        actionnaires: safeData.actionnaires,
        nombreEmployes: safeData.nombreEmployes,
        organigramme: safeData.organigramme,
        
        // ============================================
        // SECTION: Patrimoine et Moyens
        // ============================================
        immobilisations: safeData.immobilisations,
        equipements: safeData.equipements,
        vehicules: safeData.vehicules,
        stocks: safeData.stocks,
        moyensTechniques: safeData.moyensTechniques,
        capaciteProduction: safeData.capaciteProduction,
        
        // ============================================
        // SECTION: Financier et Juridique
        // ============================================
        comptesBancaires: safeData.comptesBancaires,
        assurances: safeData.assurances,
        financements: safeData.financements,
        litigesEnCours: safeData.litigesEnCours,
        detailsLitiges: safeData.detailsLitiges,
        failliteAnterieure: safeData.failliteAnterieure,
        detailsFaillite: safeData.detailsFaillite,
        poursuiteJudiciaire: safeData.poursuiteJudiciaire,
        detailsPoursuites: safeData.detailsPoursuites,
        garantiePrets: safeData.garantiePrets,
        detailsGaranties: safeData.detailsGaranties,
        antecedentesFiscales: safeData.antecedentesFiscales,
        detailsAntecedentes: safeData.detailsAntecedentes,
        
        // ============================================
        // SECTION: Documents Obligatoires
        // ============================================
        documentsEntreprise: safeData.documentsEntreprise,
        documentsPersonnel: safeData.documentsPersonnel,
        documentsFinanciers: safeData.documentsFinanciers,
        documentsPatrimoine: safeData.documentsPatrimoine,
        documentsProprietéIntellectuelle: safeData.documentsProprietéIntellectuelle,
        documentsSectoriels: safeData.documentsSectoriels,
      };
      
      // Le retour compte : `updateCompany` rend `null` quand elle refuse
      // d'envoyer (champs manquants). Enchainer sans le lire effacait le
      // brouillon et fermait l'edition alors que RIEN n'avait ete enregistre.
      // Le formulaire part tel quel : sa forme sur le fil (null pour une
      // localisation vide, '' pour un choix effacé, variant, fichiers de
      // FileUpload...) déborde encore du type partagé Company.
      const enregistree = await updateCompany(updateData);
      if (!enregistree) {
        setIsSaving(false);
        return;
      }

      // ── Juridiction → profil CLIENT (customer-service) ──────────────────
      // Persiste Ville/Province sur Customer.address pour l'isolation régionale
      // admin + le placement sur cartes. Complémentaire au GPS. NON bloquant :
      // un échec ici ne doit pas invalider la sauvegarde de l'entreprise.
      const jurisdictionCustomerId =
        user?.customerId || companyExtras?.customerId || (enregistree as (Company & CompanyServerExtras) | null)?.customerId || undefined;
      // AVEC : son lieu vit dans avecDetails, pas sur Customer.address.
      const jurisdiction = !isAvec && (safeData.province || safeData.ville)
        ? { city: safeData.ville || undefined, province: safeData.province || undefined }
        : null;
      if (jurisdiction && jurisdictionCustomerId) {
        try {
          await customerApiService.updateJurisdiction(jurisdictionCustomerId, jurisdiction);
          console.log('✅ Juridiction (Ville/Province) persistée sur le profil client');
        } catch (jurisdictionError) {
          console.warn(
            '⚠️ Échec de la persistance de la juridiction sur le profil client (non bloquant):',
            jurisdictionError,
          );
        }
      }
      // Sauvegarde backend réussie → le brouillon local n'a plus lieu d'être.
      clearFormDraft(draftKey);
      setIsEditing(false);
    } catch (error) {
      // Jamais en silence : le bouton redevient actif et la raison s'affiche.
      console.error('Erreur lors de la sauvegarde:', error);
      showToast({
        type: 'error',
        title: 'Échec de la sauvegarde',
        message: error instanceof Error ? error.message : 'La sauvegarde a échoué. Réessayez.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Gestion des champs individuels
  // Setter générique alimenté par des widgets hétérogènes (EditableField rend
  // string | number, FinancialInput ses propres formes...) : la clé est
  // vérifiée, la valeur est recopiée telle quelle.
  const handleFieldChange = async (field: keyof typeof formData, value: unknown) => {
    if (!isEditing) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Prêts et levées de fonds partagent `financements` (un seul tableau côté
  // serveur) : chaque section ne remplace que sa part, à partir de l'état le
  // plus récent, pour qu'enregistrer l'une n'efface jamais l'autre.
  const saveFinancingSection = (section: FinancingSection, items: Financing[]) => {
    if (!isEditing) return;
    setFormData(prev => ({
      ...prev,
      financements: mergeFinancingSection(prev.financements, section, items, prev.typeEntreprise === 'startup'),
    }));
  };

  // Options sectorielles dynamiques
  const getSecteurOptions = () => {
    return formData.typeEntreprise === 'startup' ? STARTUP_SECTORS : TRADITIONAL_SECTORS;
  };

  // ✨ ANTI-VIBRATION: Afficher un loader stable pendant le chargement initial
  // Conditions claires: user en chargement OU (companyId existe mais pas encore chargé)
  const isInitialLoading = userLoading || (user?.companyId && !company && companyLoading);
  
  // ✨ ANTI-VIBRATION: Attendre que les données soient prêtes avant d'afficher le formulaire
  // Si l'utilisateur a une entreprise, attendre qu'elle soit chargée ET initialisée
  const waitingForData = user?.companyId && !isFormInitialized;
  
  if (isInitialLoading || waitingForData) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#197ca8]" />
          <p className="mt-4 text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* Toggle PME / Coopérative — uniquement pour les nouveaux comptes,
          avant création de la company en DB. Une fois la company créée,
          son variant prime via le backend et ce toggle disparaît. */}
      {isNewAccount && (
        <div className="mb-4 grid grid-cols-3 gap-1 rounded-md bg-gray-100 p-1 w-full max-w-sm">
          {([
            ['standard', 'PME'],
            ['cooperative', 'Coopérative'],
            ['avec', 'AVEC'],
          ] as [EntityVariant, string][]).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => handleVariantChange(value)}
              className={`px-3 py-2 text-xs sm:text-sm font-semibold text-center rounded-md transition-colors ${
                pendingVariant === value
                  ? 'bg-white text-primary shadow-sm'
                  : 'bg-transparent text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* En-tête avec boutons d'édition */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6 pb-4 border-b border-gray-200">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
            {vocab.formTitle}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Informations détaillées sur {vocab.yourEntity}
          </p>
          {companyLoading && isFormInitialized && (
            <div className="flex items-center text-sm text-[#197ca8] mt-1">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Synchronisation...
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          {!isEditing ? (
            <button
              onClick={handleStartEditing}
              className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors"
            >
              <Edit3 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Éditer le profil</span>
              <span className="sm:hidden">Éditer</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEditing}
                disabled={isSaving}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Annuler</span>
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                {/* Texte enveloppe : un noeud texte nu que la traduction
                    automatique du navigateur remplace faisait planter React
                    (« insertBefore … not a child », vu sur /company). */}
                <span>{isSaving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
              </button>
            </>
          )}
        </div>
      </div>

      {isAvec ? (
        <AvecIdentificationSection
          values={avecValuesOf(formData)}
          accountEmail={accountEmail}
          isEditing={isEditing}
          onChange={handleAvecFieldChange}
        />
      ) : (
      <Tabs defaultValue="general">
        <TabsList className={entrepriseCreee ? 'mb-8' : 'mb-3'}>
          <TabsTrigger value="general">Général</TabsTrigger>
          <TabsTrigger value="patrimoine" disabled={!entrepriseCreee}>Patrimoine</TabsTrigger>
          <TabsTrigger value="structure" disabled={!entrepriseCreee}>Structure</TabsTrigger>
          <TabsTrigger value="financier" disabled={!entrepriseCreee}>Finance et juridique</TabsTrigger>
          <TabsTrigger value="localisation" disabled={!entrepriseCreee}>Localisation</TabsTrigger>
          <TabsTrigger value="presentation" disabled={!entrepriseCreee}>Pitch</TabsTrigger>
        </TabsList>
        {!entrepriseCreee && (
          <p className="mb-8 flex items-start gap-2 text-sm text-gray-500">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#197ca8]" />
            <span>
              Pour créer {vocab.yourEntity}, l'onglet Général suffit : le nom et la forme juridique, puis Sauvegarder.
              Les autres onglets s'ouvrent ensuite.
            </span>
          </p>
        )}

        {/* Section I: Renseignements Généraux */}
        <TabsContent value="general">
          <div className="space-y-8">
            {/* 1. Identification de base */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Identification de Base
              </h2>
              
              {/* Logo de l'entité */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {vocab.logoLabel}
                </label>
                {/* Si l'entité a un UUID valide, utiliser CompanyLogo */}
                {company?.id && !company.id.includes('temp') ? (
                  <CompanyLogo
                    companyId={company.id}
                    logo={company?.logo}
                    name={formData.raisonSociale || (vocab.variant === 'cooperative' ? 'Votre Coopérative' : 'Votre Entreprise')}
                    onLogoUpdate={(success) => {
                      if (success) {
                        console.log('Logo mis à jour avec succès');
                      }
                    }}
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-200 flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-gray-400" />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Info className="w-4 h-4 text-blue-500" />
                      <span>{vocab.saveBeforeLogo}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label="Raison sociale ou dénomination *"
                  value={formData.raisonSociale}
                  onSave={(value) => handleFieldChange('raisonSociale', value)}
                  type="text"
                  required
                  disabled={!isEditing}
                />
                <EditableField
                  label="Sigle"
                  value={formData.sigle}
                  onSave={(value) => handleFieldChange('sigle', value)}
                  type="text"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Forme juridique selon l'OHADA *"
                  value={formData.formeJuridiqueOHADA}
                  onSave={(value) => handleFieldChange('formeJuridiqueOHADA', value)}
                  type="select"
                  options={[...getLegalFormsForVariant(vocab.variant)]}
                  required
                  disabled={!isEditing}
                />
                <EditableField
                  label="Date de création"
                  value={formData.dateCreation}
                  onSave={(value) => handleFieldChange('dateCreation', value)}
                  type="date"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Date de début effectif des activités"
                  value={formData.dateDebutActivites}
                  onSave={(value) => handleFieldChange('dateDebutActivites', value)}
                  type="date"
                  disabled={!isEditing}
                />
                <EditableField
                  label={vocab.sizeLabel}
                  value={formData.tailleEntreprise}
                  onSave={(value) => handleFieldChange('tailleEntreprise', value)}
                  type="select"
                  options={[...getCompanySizesForVariant(vocab.variant)]}
                  disabled={!isEditing}
                />
                {/* Contact propage dans toutes les apps. Meme valeur que
                    « Téléphone de contact » de l'onglet Localisation. */}
                <PhoneField
                  label="Téléphone de contact"
                  value={formData.telephoneMobile}
                  onChange={(value) => handleFieldChange('telephoneMobile', value)}
                  disabled={!isEditing}
                  error={isEditing ? phoneError(formData.telephoneMobile) : null}
                  hint={`Avec l'indicatif du pays. Le numéro qui joint ${vocab.yourEntity} dans les applications Wanzo.`}
                />
              </div>
            </section>

            {/* 2. Immatriculations */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Immatriculations et Identifications
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <EditableField
                  label="Numéro RCCM"
                  value={formData.numeroRCCM}
                  onSave={(value) => handleFieldChange('numeroRCCM', value)}
                  type="text"
                  placeholder="CD/KIN/RCCM/YY-X-NNNNN"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Numéro d'identification nationale"
                  value={formData.numeroIdentificationNationale}
                  onSave={(value) => handleFieldChange('numeroIdentificationNationale', value)}
                  type="text"
                  placeholder="NAT/KIN/YYYY/NNNNN"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Numéro d'impôt fiscal"
                  value={formData.numeroImpotFiscal}
                  onSave={(value) => handleFieldChange('numeroImpotFiscal', value)}
                  type="text"
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* 2 bis. Juridiction (localisation administrative) — optionnelle.
                Province + Ville (RDC par défaut). Complémentaire aux coordonnées
                GPS de l'onglet Localisation : ces valeurs sont persistées sur le
                profil CLIENT (Customer.address.province/city) et alimentent
                l'isolation régionale des administrateurs + le placement sur cartes. */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Juridiction (localisation administrative)
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Province et ville de rattachement (facultatif). Permet de situer
                {vocab.variant === 'cooperative' ? ' la coopérative' : " l'entreprise"} sur les cartes
                et l'isolation régionale, même sans coordonnées GPS précises.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label="Province"
                  value={formData.province}
                  onSave={(value) => {
                    if (!isEditing) return Promise.resolve();
                    // Changer de province réinitialise la ville (listes liées).
                    return new Promise<void>((resolve) => {
                      setFormData(prev => ({
                        ...prev,
                        province: String(value),
                        ville: '',
                      }));
                      setTimeout(() => resolve(), 0);
                    });
                  }}
                  type="select"
                  options={getProvinces().map(p => ({ value: p.value, label: p.label }))}
                  disabled={!isEditing}
                />
                <EditableField
                  label="Ville"
                  value={formData.ville}
                  onSave={(value) => handleFieldChange('ville', value)}
                  type="select"
                  options={getCities(formData.province).map(c => ({ value: c, label: c }))}
                  disabled={!isEditing || !formData.province}
                  placeholder={formData.province ? 'Sélectionner...' : "Choisir d'abord une province"}
                />
              </div>
            </section>

            {/* 3. Nature des activités */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. Nature des Activités
              </h2>
              
              <div className="space-y-6">
                {/* Toggle "Type d'entreprise" (traditional/startup) — concept
                    PME-only. Pour une coopérative, ce choix n'a pas de sens :
                    une coopérative est ni "traditionnelle" ni "startup" au
                    sens entrepreneurial du terme. On hide pour les coops ;
                    typeEntreprise reste implicitement à 'traditional' en DB
                    pour compat (les secteurs TRADITIONAL_SECTORS suffisent). */}
                {vocab.variant !== 'cooperative' && (
                  <EditableField
                    label="Type d'entreprise *"
                    value={formData.typeEntreprise}
                    onSave={async (value) => {
                      if (!isEditing) return Promise.resolve();
                      const newType = value as 'traditional' | 'startup';

                      // Déterminer les secteurs disponibles pour le nouveau type
                      const newSectorOptions = newType === 'startup' ? STARTUP_SECTORS : TRADITIONAL_SECTORS;
                      const currentSector = formData.secteurActivitePrincipal;

                      // Vérifier si le secteur actuel existe dans les nouvelles options
                      const sectorExists = newSectorOptions.some(opt => opt.value === currentSector);

                      return new Promise<void>((resolve) => {
                        setFormData(prev => ({
                          ...prev,
                          typeEntreprise: newType,
                          // Réinitialiser le secteur si incompatible avec le nouveau type
                          secteurActivitePrincipal: sectorExists ? currentSector : '',
                          // Réinitialiser aussi les secteurs secondaires si incompatibles
                          secteursActiviteSecondaires: sectorExists ? prev.secteursActiviteSecondaires : []
                        }));
                        // Laisser React mettre à jour l'état avant de résoudre
                        setTimeout(() => resolve(), 0);
                      });
                    }}
                    type="select"
                    options={[...COMPANY_TYPES]}
                    disabled={!isEditing}
                  />
                )}

                <EditableField
                  label="Secteur d'activité principal"
                  value={formData.secteurActivitePrincipal}
                  onSave={(value) => handleFieldChange('secteurActivitePrincipal', value)}
                  type="select"
                  options={[...getSecteurOptions()]}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Secteurs d'activité secondaires"
                  value={formData.secteursActiviteSecondaires}
                  onSave={(tags) => handleFieldChange('secteursActiviteSecondaires', tags)}
                  disabled={!isEditing}
                  placeholder="Ajoutez des secteurs secondaires"
                  suggestions={getSecteurOptions().map(opt => opt.label)}
                />

                <div>
                  <TagInput
                    label="Secteurs personnalisés"
                    value={formData.secteursPersonalises}
                    onSave={(tags) => handleFieldChange('secteursPersonalises', tags)}
                    disabled={!isEditing}
                    placeholder="Ajoutez vos propres secteurs d'activité"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Vous pouvez ajouter des secteurs qui ne sont pas dans notre liste prédéfinie
                  </p>
                </div>

                <EditableField
                  label="Description détaillée des activités"
                  value={formData.descriptionActivites}
                  onSave={(value) => handleFieldChange('descriptionActivites', value)}
                  type="textarea"
                  disabled={!isEditing}
                />

                <TagInput
                  label="Produits/Services offerts"
                  value={formData.produitsServices}
                  onSave={(tags) => handleFieldChange('produitsServices', tags)}
                  disabled={!isEditing}
                  placeholder="Ajoutez vos produits et services"
                />
              </div>
            </section>

            {/* 4. Capital social / Capital variable */}
            {/* En droit OHADA des coopératives, le capital est "variable" et
                représenté par des "parts sociales" — pas un capital social
                figé comme dans les sociétés commerciales. On adapte uniquement
                le libellé, la donnée reste stockée dans le même champ. */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                4. {vocab.variant === 'cooperative'
                  ? 'Capital Variable et Parts Sociales'
                  : 'Capital Social'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label={vocab.variant === 'cooperative'
                    ? 'Montant du capital variable'
                    : 'Montant du capital social'}
                  value={formData.capitalSocial}
                  onSave={(value) => handleFieldChange('capitalSocial', value)}
                  type="number"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Devise"
                  value={formData.deviseCapital}
                  onSave={(value) => handleFieldChange('deviseCapital', value)}
                  type="select"
                  options={[...CURRENCIES]}
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* 5. Incubation et Accélération (optionnel) — masqué pour les
                coopératives : incubateurs/accélérateurs sont des dispositifs
                d'écosystème startup. Une coopérative de détaillants/artisans
                ne participe jamais à un parcours d'incubation. */}
            {vocab.variant !== 'cooperative' && (
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                5. Incubation et Accélération (optionnel)
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Si {vocab.yourEntity} bénéficie d'un accompagnement par un incubateur ou accélérateur, veuillez renseigner ces informations.
              </p>
              
              <div className="space-y-6">
                <EditableField
                  label="Votre entreprise est-elle actuellement en incubation ou accélération ?"
                  value={formData.enIncubation ? 'oui' : 'non'}
                  onSave={async (value) => {
                    if (!isEditing) return Promise.resolve();
                    const isIncubated = value === 'oui';
                    return new Promise<void>((resolve) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        enIncubation: isIncubated,
                        // Reset des champs si pas en incubation
                        typeAccompagnement: isIncubated ? prev.typeAccompagnement : '',
                        nomIncubateurAccelerateur: isIncubated ? prev.nomIncubateurAccelerateur : '',
                        certificatAffiliation: isIncubated ? prev.certificatAffiliation : []
                      }));
                      // Laisser React mettre à jour l'état avant de résoudre
                      setTimeout(() => resolve(), 0);
                    });
                  }}
                  type="select"
                  options={[...YES_NO_OPTIONS]}
                  disabled={!isEditing}
                />

                {formData.enIncubation && (
                  <>
                    <EditableField
                      label="Type d'accompagnement"
                      value={formData.typeAccompagnement}
                      onSave={(value) => handleFieldChange('typeAccompagnement', value)}
                      type="select"
                      options={[...ACCOMPANIMENT_TYPES]}
                      disabled={!isEditing}
                    />

                    <EditableField
                      label="Nom de l'incubateur ou accélérateur"
                      value={formData.nomIncubateurAccelerateur}
                      onSave={(value) => handleFieldChange('nomIncubateurAccelerateur', value)}
                      type="text"
                      disabled={!isEditing}
                      placeholder="ex: CTIC Dakar, I&P, etc."
                    />

                    <FileUpload
                      label="Certificat d'affiliation"
                      description="Certificat ou attestation d'affiliation à l'incubateur/accélérateur (PDF)"
                      value={formData.certificatAffiliation}
                      onSave={(files) => handleFieldChange('certificatAffiliation', files)}
                      disabled={!isEditing}
                      multiple={false}
                      acceptedTypes={['.pdf']}
                      maxSize={5}
                    />
                  </>
                )}
              </div>
            </section>
            )}

            {/* Questions spécifiques selon le type */}
            {formData.typeEntreprise === 'startup' && (
              <section className="bg-blue-50 rounded-lg border border-blue-200 p-6">
                <h2 className="text-lg font-medium text-blue-900 mb-4">
                  Spécificités Startup Technologique
                </h2>
                <div className="space-y-6">
                  <EditableField
                    label="Niveau de maturité technologique (TRL)"
                    value={formData.niveauMaturiteTechnologique}
                    onSave={(value) => handleFieldChange('niveauMaturiteTechnologique', value)}
                    type="select"
                    options={[...TECHNOLOGY_READINESS_LEVELS]}
                    disabled={!isEditing}
                  />

                  <EditableField
                    label="Modèle économique"
                    value={formData.modeleEconomique}
                    onSave={(value) => handleFieldChange('modeleEconomique', value)}
                    type="select"
                    options={[...BUSINESS_MODELS]}
                    disabled={!isEditing}
                  />

                  <TagInput
                    label="Propriété intellectuelle"
                    value={formData.proprieteIntellectuelle}
                    onSave={(tags) => handleFieldChange('proprieteIntellectuelle', tags)}
                    disabled={!isEditing}
                    placeholder="Brevets, marques, droits d'auteur..."
                    suggestions={['Brevet', 'Marque déposée', 'Droit d\'auteur', 'Secret commercial', 'Design industriel']}
                  />
                </div>
              </section>
            )}

            {formData.typeEntreprise === 'traditional' && (
              <section className="bg-green-50 rounded-lg border border-green-200 p-6">
                <h2 className="text-lg font-medium text-green-900 mb-4">
                  {vocab.variant === 'cooperative'
                    ? 'Certifications et Licences'
                    : 'Spécificités Entreprise Traditionnelle'}
                </h2>
                <div className="space-y-6">
                  <EditableField
                    label="Certification qualité"
                    value={formData.certificationQualite ? 'oui' : 'non'}
                    onSave={(value) => handleFieldChange('certificationQualite', value === 'oui')}
                    type="select"
                    options={[...YES_NO_OPTIONS]}
                    disabled={!isEditing}
                  />

                  <TagInput
                    label="Licences d'exploitation spécifiques"
                    value={formData.licencesExploitation}
                    onSave={(tags) => handleFieldChange('licencesExploitation', tags)}
                    disabled={!isEditing}
                    placeholder="Licences sectorielles, autorisations..."
                    suggestions={['Licence d\'importation', 'Autorisation sanitaire', 'Permis d\'exploitation', 'Certification ISO']}
                  />
                </div>
              </section>
            )}
          </div>
        </TabsContent>

        {/* Section II: Localisation & Contacts */}
        <TabsContent value="localisation">
          <div className="space-y-8">
            {/* Localisation */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Localisation des Sites
              </h2>
              <div className="space-y-6">
                <LocationPicker
                  label="Siège social *"
                  value={formData.siegeSocial}
                  onSave={(coords) => handleFieldChange('siegeSocial', withPickedLocation(formData.siegeSocial, coords))}
                  disabled={!isEditing}
                />
                
                <LocationPicker
                  label="Siège d'exploitation"
                  value={formData.siegeExploitation}
                  onSave={(coords) => handleFieldChange('siegeExploitation', coords)}
                  disabled={!isEditing}
                />

                <MultiLocationPicker
                  label="Unités de production"
                  description="Localisations des sites de production, usines, ateliers, etc."
                  value={formData.unitesProduction}
                  onChange={(locations) => handleFieldChange('unitesProduction', locations)}
                  disabled={!isEditing}
                  maxLocations={5}
                  placeholder="Ajouter une unité de production"
                />

                <MultiLocationPicker
                  label="Points de vente"
                  description="Localisations des magasins, boutiques, showrooms, bureaux commerciaux, etc."
                  value={formData.pointsVente}
                  onChange={(locations) => handleFieldChange('pointsVente', locations)}
                  disabled={!isEditing}
                  maxLocations={10}
                  placeholder="Ajouter un point de vente"
                />
              </div>
            </section>

            {/* Contacts */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Informations de Contact
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Adresse physique : imprimée sur les factures, reçus et tickets des apps. */}
                <div className="md:col-span-2">
                  <EditableField
                    label="Adresse physique du siège"
                    placeholder="Avenue, numéro, quartier, commune, ville"
                    value={typedSiegeAddress(formData.siegeSocial)}
                    onSave={async (value) => handleFieldChange('siegeSocial', withSiegeAddress(formData.siegeSocial, String(value)))}
                    type="text"
                    disabled={!isEditing}
                  />
                </div>
                <EditableField
                  label="Téléphone fixe"
                  value={formData.telephoneFixe}
                  onSave={(value) => handleFieldChange('telephoneFixe', value)}
                  type="tel"
                  disabled={!isEditing}
                />
                <PhoneField
                  label="Téléphone de contact"
                  value={formData.telephoneMobile}
                  onChange={(value) => handleFieldChange('telephoneMobile', value)}
                  disabled={!isEditing}
                  error={isEditing ? phoneError(formData.telephoneMobile) : null}
                  hint="Repris de l'onglet Général."
                />
                <EditableField
                  label="Fax"
                  value={formData.fax}
                  onSave={(value) => handleFieldChange('fax', value)}
                  type="tel"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Email"
                  value={formData.email}
                  onSave={(value) => handleFieldChange('email', value)}
                  type="email"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Boîte postale"
                  value={formData.boitePostale}
                  onSave={(value) => handleFieldChange('boitePostale', value)}
                  type="text"
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* Réseaux sociaux */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. Présence Numérique
              </h2>
              <SocialLinksInput
                label="Site web et réseaux sociaux"
                value={formData.reseauxSociaux}
                onSave={(links) => handleFieldChange('reseauxSociaux', links)}
                disabled={!isEditing}
              />
            </section>
          </div>
        </TabsContent>

        {/* Section III: Pitch & Présentation */}
        <TabsContent value="presentation">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {vocab.pitchTitle}
              </h2>
              <PitchSection
                label="Présentation commerciale"
                value={formData.pitch}
                onSave={(pitch) => handleFieldChange('pitch', pitch)}
                disabled={!isEditing}
              />
            </section>
          </div>
        </TabsContent>

        {/* Sections restantes - maintenant implémentées */}
        <TabsContent value="patrimoine">
          <div className="space-y-8">
            {/* Immobilisations */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Immobilisations
              </h2>
              <AssetInput
                label="Biens immobiliers"
                value={formData.immobilisations}
                onSave={(assets) => handleFieldChange('immobilisations', assets)}
                disabled={!isEditing}
                assetType="immobilier"
                placeholder="Aucun bien immobilier enregistré"
              />
            </section>

            {/* Équipements */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Équipements et Matériels
              </h2>
              <AssetInput
                label="Équipements de production et matériels"
                value={formData.equipements}
                onSave={(assets) => handleFieldChange('equipements', assets)}
                disabled={!isEditing}
                assetType="equipement"
                placeholder="Aucun équipement enregistré"
              />
            </section>

            {/* Véhicules */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. Parc Automobile
              </h2>
              <AssetInput
                label="Véhicules et moyens de transport"
                value={formData.vehicules}
                onSave={(assets) => handleFieldChange('vehicules', assets)}
                disabled={!isEditing}
                assetType="vehicule"
                placeholder="Aucun véhicule enregistré"
              />
            </section>

            {/* Stocks */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                4. Stocks et Inventaires (Actifs Circulants)
              </h2>
              <StockInput
                label="Stocks de matières premières et produits finis"
                value={formData.stocks}
                onSave={(stocks) => handleFieldChange('stocks', stocks)}
                disabled={!isEditing}
                placeholder="Aucun stock enregistré"
              />
            </section>

            {/* Moyens techniques */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                5. Moyens Techniques et Capacités
              </h2>
              <div className="space-y-6">
                <TagInput
                  label="Moyens techniques et technologiques"
                  value={formData.moyensTechniques}
                  onSave={(tags) => handleFieldChange('moyensTechniques', tags)}
                  disabled={!isEditing}
                  placeholder="Logiciels, systèmes, outils techniques..."
                  suggestions={['ERP', 'CRM', 'Site web', 'Application mobile', 'Base de données', 'Système de sécurité']}
                />

                <EditableField
                  label="Capacité de production"
                  value={formData.capaciteProduction}
                  onSave={(value) => handleFieldChange('capaciteProduction', value)}
                  type="textarea"
                  disabled={!isEditing}
                  placeholder="Décrivez vos capacités de production actuelles et potentielles"
                />
              </div>
            </section>
          </div>
        </TabsContent>

        <TabsContent value="structure">
          <div className="space-y-8">
            {/* Dirigeants */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Équipe Dirigeante
              </h2>
              <PersonInput
                label="Dirigeants et responsables"
                value={formData.dirigeants}
                onSave={(persons) => handleFieldChange('dirigeants', persons)}
                disabled={!isEditing}
                personType="dirigeant"
                placeholder="Aucun dirigeant enregistré"
                companyType={formData.typeEntreprise}
              />
            </section>

            {/* Actionnaires — masqué pour les coopératives. En droit OHADA
                des sociétés coopératives, on parle de "membres associés" /
                "coopérants" (déjà gérés via le module BusinessUnit côté
                accounting), pas d'actionnaires détenant du capital. La
                section Dirigeants ci-dessus suffit à représenter la
                gouvernance d'une coopérative. */}
            {vocab.variant !== 'cooperative' && (
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Structure Actionnariale
              </h2>
              <PersonInput
                label="Actionnaires et associés"
                value={formData.actionnaires}
                onSave={(persons) => handleFieldChange('actionnaires', persons)}
                disabled={!isEditing}
                personType="actionnaire"
                showEquity={true}
                formeJuridique={formData.formeJuridiqueOHADA}
                placeholder="Aucun actionnaire enregistré"
                companyType={formData.typeEntreprise}
              />
            </section>
            )}

            {/* Personnel — pour une coopérative, ce sont les "coopérants" et
                non les "employés". On adapte uniquement le libellé via vocab. */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. {vocab.variant === 'cooperative' ? 'Coopérants et Personnel' : 'Personnel et Employés'}
              </h2>
              <div className="space-y-6">
                <EditableField
                  label={`Nombre total ${vocab.variant === 'cooperative' ? 'de coopérants' : "d'employés"}`}
                  value={formData.nombreEmployes.toString()}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, nombreEmployes: parseInt(value.toString()) || 0 }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <PersonInput
                  label="Employés clés"
                  value={formData.employes}
                  onSave={(persons) => handleFieldChange('employes', persons)}
                  disabled={!isEditing}
                  personType="employe"
                  showContract={true}
                  placeholder="Aucun employé clé enregistré"
                  companyType={formData.typeEntreprise}
                />
              </div>
            </section>

            {/* Organigramme */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                4. Organisation Interne
              </h2>
              <EditableField
                label="Description de l'organigramme"
                value={formData.organigramme}
                onSave={(value) => handleFieldChange('organigramme', value)}
                type="textarea"
                disabled={!isEditing}
                placeholder="Décrivez la structure organisationnelle de votre entreprise"
              />
            </section>
          </div>
        </TabsContent>

        <TabsContent value="financier">
          <div className="space-y-8">
            {/* Informations bancaires */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <FinancialInput
                label="Références Bancaires"
                type="bank-accounts"
                value={formData.comptesBancaires}
                onSave={(accounts) => handleFieldChange('comptesBancaires', accounts)}
                disabled={!isEditing}
                companyType={formData.typeEntreprise}
              />
            </section>

            {/* Financements traditionnels */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <FinancialInput
                label="Concours Financiers et Prêts"
                type="loans"
                value={itemsForSection(formData.financements, 'loans', formData.typeEntreprise === 'startup')}
                onSave={(loans) => saveFinancingSection('loans', loans)}
                disabled={!isEditing}
                companyType={formData.typeEntreprise}
              />
            </section>

            {/* Levées de fonds pour startups */}
            {formData.typeEntreprise === 'startup' && (
              <section className="bg-white rounded-lg border border-gray-200 p-6">
                <FinancialInput
                  label="Levées de Fonds"
                  type="funding-rounds"
                  value={itemsForSection(formData.financements, 'funding-rounds', true)}
                  onSave={(rounds) => saveFinancingSection('funding-rounds', rounds)}
                  disabled={!isEditing}
                  companyType={formData.typeEntreprise}
                />
              </section>
            )}

            {/* Aspects juridiques */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <Scale className="text-red-500 mr-2 h-5 w-5" />
                Aspects Juridiques et Réglementaires
              </h2>
              
              <div className="space-y-6">
                {/* Faillite ou insolvabilité */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Avez-vous déjà fait faillite ou préparez-vous une demande de faillite/insolvabilité ?"
                    value={formData.failliteAnterieure ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, failliteAnterieure: value === 'oui' }));
                    }}
                    type="select"
                    options={[...YES_NO_OPTIONS]}
                    disabled={!isEditing}
                  />
                  
                  {formData.failliteAnterieure && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails de la faillite"
                        value={formData.detailsFaillite}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsFaillite: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Décrivez les circonstances et l'état actuel..."
                      />
                    </div>
                  )}
                </div>

                {/* Poursuites judiciaires */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Vous-même ou votre entreprise faites-vous l'objet de poursuites judiciaires ?"
                    value={formData.poursuiteJudiciaire ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, poursuiteJudiciaire: value === 'oui' }));
                    }}
                    type="select"
                    options={[...YES_NO_OPTIONS]}
                    disabled={!isEditing}
                  />
                  
                  {formData.poursuiteJudiciaire && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails des poursuites"
                        value={formData.detailsPoursuites}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsPoursuites: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Nature des poursuites, état d'avancement, montants en jeu..."
                      />
                    </div>
                  )}
                </div>

                {/* Garanties de prêts */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Êtes-vous actuellement garant d'un ou plusieurs prêts ?"
                    value={formData.garantiePrets ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, garantiePrets: value === 'oui' }));
                    }}
                    type="select"
                    options={[...YES_NO_OPTIONS]}
                    disabled={!isEditing}
                  />
                  
                  {formData.garantiePrets && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails des garanties"
                        value={formData.detailsGaranties}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsGaranties: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Montants garantis, bénéficiaires, échéances..."
                      />
                    </div>
                  )}
                </div>

                {/* Antécédents fiscaux */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Avez-vous eu des antécédents avec l'administration fiscale ?"
                    value={formData.antecedentesFiscales ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, antecedentesFiscales: value === 'oui' }));
                    }}
                    type="select"
                    options={[...YES_NO_OPTIONS]}
                    disabled={!isEditing}
                  />
                  
                  {formData.antecedentesFiscales && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails des antécédents fiscaux"
                        value={formData.detailsAntecedentes}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsAntecedentes: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Nature des problèmes, résolution, situation actuelle..."
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Documents obligatoires */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <FolderOpen className="text-blue-500 mr-2 h-5 w-5" />
                Documents Obligatoires
              </h2>
              
              <div className="space-y-8">
                {/* Documents entreprise */}
                <div>
                  <FileUpload
                    label="Documents Entreprise"
                    description="Statuts, RCCM, autorisations, attestations fiscales"
                    value={formData.documentsEntreprise}
                    onSave={(files) => handleFieldChange('documentsEntreprise', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={10}
                  />
                </div>

                {/* Documents personnel */}
                <div>
                  <FileUpload
                    label="Documents Personnel"
                    description="CV des dirigeants, cartes d'identité, procurations"
                    value={formData.documentsPersonnel}
                    onSave={(files) => handleFieldChange('documentsPersonnel', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={5}
                  />
                </div>

                {/* Documents financiers */}
                <div>
                  <FileUpload
                    label={formData.typeEntreprise === 'startup' ? 'Documents Financiers et Business Plan' : 'États Financiers'}
                    description={
                      formData.typeEntreprise === 'startup' 
                        ? 'Business plan, projections financières, pitch deck, term sheets'
                        : 'États financiers certifiés des 3 derniers exercices (format OHADA)'
                    }
                    value={formData.documentsFinanciers}
                    onSave={(files) => handleFieldChange('documentsFinanciers', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx']}
                    maxSize={20}
                  />
                </div>

                {/* Documents patrimoine */}
                <div>
                  <FileUpload
                    label="Documents Patrimoine et Équipements"
                    description="Factures d'équipements, contrats de licence, certificats de propriété"
                    value={formData.documentsPatrimoine}
                    onSave={(files) => handleFieldChange('documentsPatrimoine', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={10}
                  />
                </div>

                {/* Documents propriété intellectuelle (surtout startups) */}
                {formData.typeEntreprise === 'startup' && (
                  <div>
                    <FileUpload
                      label="Documents Propriété Intellectuelle"
                      description="Brevets, marques, NDA, accords de transfert IP"
                      value={formData.documentsProprietéIntellectuelle}
                      onSave={(files) => handleFieldChange('documentsProprietéIntellectuelle', files)}
                      disabled={!isEditing}
                      multiple={true}
                      acceptedTypes={['.pdf', '.doc', '.docx']}
                      maxSize={10}
                    />
                  </div>
                )}

                {/* Documents sectoriels */}
                <div>
                  <FileUpload
                    label="Documents Sectoriels Spécifiques"
                    description="Licences sectorielles, certifications qualité, autorisations spécialisées"
                    value={formData.documentsSectoriels}
                    onSave={(files) => handleFieldChange('documentsSectoriels', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={15}
                  />
                </div>
              </div>
            </section>
          </div>
        </TabsContent>
      </Tabs>
      )}

      {/* Section d'extraction PDF (profil complet, sans objet pour une AVEC) */}
      {!isAvec && (
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Extraction du formulaire
              </h3>
              <p className="text-sm text-gray-600">
                Téléchargez votre formulaire d'identification au format PDF pour archivage ou soumission externe.
              </p>
            </div>
            <div className="flex-shrink-0">
              <ProfessionalPDFExtractionButton
                type="company"
                formData={formData}
                entityName={formData.raisonSociale || company?.raisonSociale || "Mon entreprise"}
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default EnterpriseIdentificationForm;